import { Alert, Form, Spin } from 'antd'
import React, { FunctionComponent, useMemo } from 'react'

import { BuybackFormValues } from '../../../common/buybackTypes'
import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { RuianUnitResponse } from '../../../common/ruianTypes'
import {
  UnitRent,
  useUnitRentByCadastralAreaCodeQuery,
} from '../../../graphql/generated'
import { useAppSelector } from '../../../redux/hooks'
import { BUYBACK_INITIAL_VALUES } from '../../utils/buybackConst'
import { usePropertyDetailTitle } from '../../utils/usePropertyDetailTitle'
import BuybackForm from '../BuybackForm/BuybackForm'
import { PageLayoutTabContainer } from '../PageLayoutTabContainer/PageLayoutTabContainer'
import { PropertyDetailBuybackHistoryTable } from '../PropertyDetailBuybackHistoryTable/PropertyDetailBuybackHistoryTable'

export interface PropertyDetailBuybackProps {
  propertyId: string
}

export const PropertyDetailBuyback: FunctionComponent<PropertyDetailBuybackProps> =
  props => {
    const ruian = useAppSelector(state => state.property.ruian)
    const drm = useAppSelector(state => state.property.drm)

    const [form] = Form.useForm<BuybackFormValues>()
    const propertyName = usePropertyDetailTitle()

    const [unitRent] = useUnitRentByCadastralAreaCodeQuery({
      pause: !drm.data?.katastralniUzemi?.kod,
      variables: {
        cadastralAreaCode: `${drm.data?.katastralniUzemi?.kod || ''}`,
      },
    })

    const initialFormValues: Partial<BuybackFormValues> = useMemo(
      () => ({
        ...BUYBACK_INITIAL_VALUES,
        cadastralAreaCode: drm.data?.katastralniUzemi?.kod || undefined,
        cadastralAreaName: drm.data?.katastralniUzemi?.nazev || undefined,
        lvNumber: drm.data?.lv || undefined,
        address: propertyName,
        propertyType: ruian.data?.type || undefined,
        constructionType: (ruian.data as RuianUnitResponse)?.druhKonstrukce
          ?.nazev,
      }),
      [
        drm.data?.katastralniUzemi?.kod,
        drm.data?.katastralniUzemi?.nazev,
        drm.data?.lv,
        propertyName,
        ruian.data,
      ]
    )

    const fetching = ruian.fetching || drm.fetching || unitRent.fetching

    const fetchingError = useMemo(() => {
      const error = ruian.error || drm.error || unitRent.error
      if (!error) return null

      let errorMessage = 'Aktualizujte stránku - '
      if (unitRent.error) errorMessage += 'Chyba při načítání cenových odhadů. '
      if (ruian.error) errorMessage += 'Chyba při načítání RUIAN dat. '
      if (drm.error) errorMessage += 'Chyba při načítání DRM dat. '

      return (
        <Alert
          type="error"
          className="!mb-6"
          showIcon
          closable
          message={errorMessage}
        />
      )
    }, [drm.error, ruian.error, unitRent.error])

    return (
      <PageLayoutTabContainer>
        {fetching ? (
          <Spin className="!flex items-center justify-center w-full h-96" />
        ) : (
          <div className="flex flex-wrap w-full gap-x-8 gap-y-16">
            <div className="w-full lg:flex-auto 3xl:w-2/3 max-w-6xl">
              {fetchingError}
              <BuybackForm
                initialValues={initialFormValues}
                propertyType={ruian.data?.type || PROPERTY_TYPES.UNIT}
                form={form}
                localRent={
                  unitRent.data?.unitRentByCadastralAreaCode as UnitRent
                }
              />
            </div>
            <div className="flex-1">
              <PropertyDetailBuybackHistoryTable
                propertyId={props.propertyId}
                form={form}
              />
            </div>
          </div>
        )}
      </PageLayoutTabContainer>
    )
  }
